/**
 * @fileoverview Dispatches Page
 * "From the Crypt" - Long-form articles and analysis from the macro watchtower.
 * 
 * Includes Data Vault section showing live pipeline infrastructure stats.
 * 
 * @module pages/dispatches
 */

import { el } from "../shared/components/simpleRender.js";
import { 
  fetchSignalsFreshness,
  fetchSignalsLatest,
  fetchMovers,
  fetchSparkline,
  fetchRankings,
  API_CONFIG 
} from "../api/capitulators.js";

// Import styles
import "../styles/dispatches.css";

// ============================================================================
// API HELPERS FOR DATA VAULT
// ============================================================================

/**
 * Fetches the full catalog with real series/signal counts
 * @returns {Promise<{series_count: number, signal_count: number, categories: Array}>}
 */
async function fetchCatalogStats() {
  const baseUrl = API_CONFIG.baseUrl;
  const response = await fetch(`${baseUrl}/catalog`);
  if (!response.ok) throw new Error(`Catalog fetch failed: ${response.status}`);
  return response.json();
}

/**
 * Fetches series list to get actual coverage dates
 * @returns {Promise<{data: Array, meta: Object}>}
 */
async function fetchSeriesStats() {
  const baseUrl = API_CONFIG.baseUrl;
  const response = await fetch(`${baseUrl}/series?limit=1000`);
  if (!response.ok) throw new Error(`Series fetch failed: ${response.status}`);
  return response.json();
}

// ============================================================================
// DATA VAULT CONFIGURATION
// ============================================================================

/**
 * CloudFront URL for data vault status files
 * TODO: Replace with actual CloudFront distribution URL when ready
 */
const DATA_VAULT_CDN = null; // e.g., "https://d1abc123xyz.cloudfront.net"

/**
 * Data source definitions with S3 paths
 */
const DATA_SOURCES = {
  fred: {
    name: "FRED",
    fullName: "Federal Reserve Economic Data",
    statusPath: "/fred/reports/latest.json",
    catalogPath: "/fred/catalog/series_catalog.json"
  },
  bls: {
    name: "BLS", 
    fullName: "Bureau of Labor Statistics",
    statusPath: "/bls/reports/status.txt"
  }
};

/**
 * Planned data sources (roadmap) - organized by category
 * Full scope of public data we'll be ingesting with descriptions
 */
const PLANNED_SOURCES = [
  // ═══════════════════════════════════════════════════════════════════════════
  // U.S. GOVERNMENT - ECONOMIC
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "EIA", 
    name: "Energy Information Administration", 
    category: "govt",
    desc: "Petroleum inventories, natural gas storage, electricity generation, renewable capacity, and energy price forecasts."
  },
  { 
    code: "CENSUS", 
    name: "U.S. Census Bureau", 
    category: "govt",
    desc: "Retail sales, housing starts, building permits, international trade, business inventories, and demographic data."
  },
  { 
    code: "TREASURY", 
    name: "U.S. Treasury Direct", 
    category: "govt",
    desc: "Auction results, yield curves, TIC flows, debt-to-the-penny, and foreign holdings of U.S. securities."
  },
  { 
    code: "BEA", 
    name: "Bureau of Economic Analysis", 
    category: "govt",
    desc: "GDP components, personal income, PCE inflation, corporate profits, and international transactions."
  },
  { 
    code: "USDA", 
    name: "Dept. of Agriculture", 
    category: "govt",
    desc: "Crop production, WASDE reports, export sales, livestock inventories, and agricultural prices."
  },
  { 
    code: "HUD", 
    name: "Housing & Urban Development", 
    category: "govt",
    desc: "FHA loan performance, housing affordability index, fair market rents, and homelessness statistics."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // U.S. GOVERNMENT - FINANCIAL REGULATORY
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "SEC", 
    name: "SEC EDGAR Filings", 
    category: "regulatory",
    desc: "13F institutional holdings, insider transactions, IPO filings, corporate actions, and financial statements."
  },
  { 
    code: "CFTC", 
    name: "Commodity Futures Trading Commission", 
    category: "regulatory",
    desc: "Commitment of Traders (COT) reports, swap dealer positions, and futures market concentration."
  },
  { 
    code: "OCC", 
    name: "Office of the Comptroller", 
    category: "regulatory",
    desc: "Bank derivatives exposure, trading revenue, credit card delinquencies, and mortgage metrics."
  },
  { 
    code: "FDIC", 
    name: "Federal Deposit Insurance Corp", 
    category: "regulatory",
    desc: "Bank call reports, deposit insurance fund, problem bank list, and quarterly banking profiles."
  },
  { 
    code: "FINRA", 
    name: "Financial Industry Regulatory Authority", 
    category: "regulatory",
    desc: "Margin debt levels, short interest data, trading halts, and broker-dealer statistics."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CENTRAL BANKS - GLOBAL
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "ECB", 
    name: "European Central Bank", 
    category: "central-bank",
    desc: "Eurozone monetary aggregates, bank lending surveys, TARGET2 balances, and inflation expectations."
  },
  { 
    code: "BOJ", 
    name: "Bank of Japan", 
    category: "central-bank",
    desc: "JGB holdings, ETF purchases, Tankan survey, and yield curve control operations."
  },
  { 
    code: "BOE", 
    name: "Bank of England", 
    category: "central-bank",
    desc: "UK monetary policy, gilt purchases, credit conditions survey, and financial stability reports."
  },
  { 
    code: "BOC", 
    name: "Bank of Canada", 
    category: "central-bank",
    desc: "Canadian overnight rate decisions, business outlook survey, and financial system review."
  },
  { 
    code: "SNB", 
    name: "Swiss National Bank", 
    category: "central-bank",
    desc: "FX intervention data, sight deposits, and Swiss franc liquidity operations."
  },
  { 
    code: "RBA", 
    name: "Reserve Bank of Australia", 
    category: "central-bank",
    desc: "Australian cash rate, commodity price index, and financial stability assessments."
  },
  { 
    code: "PBOC", 
    name: "People's Bank of China", 
    category: "central-bank",
    desc: "MLF/LPR rates, FX reserves, total social financing, and RMB exchange rate fixings."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // INTERNATIONAL ORGANIZATIONS
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "IMF", 
    name: "International Monetary Fund", 
    category: "international",
    desc: "World Economic Outlook, COFER reserves data, SDR allocations, and financial soundness indicators."
  },
  { 
    code: "BIS", 
    name: "Bank for International Settlements", 
    category: "international",
    desc: "Global liquidity indicators, cross-border banking flows, OTC derivatives, and credit gaps."
  },
  { 
    code: "OECD", 
    name: "Organisation for Economic Co-operation", 
    category: "international",
    desc: "Leading indicators, composite PMIs, unemployment harmonized data, and trade statistics."
  },
  { 
    code: "WB", 
    name: "World Bank Open Data", 
    category: "international",
    desc: "Global development indicators, commodity price forecasts, and poverty statistics."
  },
  { 
    code: "WTO", 
    name: "World Trade Organization", 
    category: "international",
    desc: "Global trade volumes, tariff data, trade disputes, and goods barometer index."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // INTERNATIONAL - NATIONAL STATISTICS
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "ESTAT", 
    name: "Eurostat", 
    category: "international",
    desc: "EU-wide GDP, HICP inflation, industrial production, and labor market statistics."
  },
  { 
    code: "STATCAN", 
    name: "Statistics Canada", 
    category: "international",
    desc: "Canadian GDP, CPI, labor force survey, and international trade data."
  },
  { 
    code: "ONS", 
    name: "UK Office for National Statistics", 
    category: "international",
    desc: "UK GDP, CPI/RPI inflation, retail sales, and labour market statistics."
  },
  { 
    code: "DESTATIS", 
    name: "German Federal Statistics", 
    category: "international",
    desc: "German GDP, industrial orders, trade balance, and ZEW sentiment indicators."
  },
  { 
    code: "NBS", 
    name: "China National Bureau of Statistics", 
    category: "international",
    desc: "Chinese GDP, PMI, fixed asset investment, retail sales, and property prices."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MARKET DATA PROVIDERS
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "CME", 
    name: "Chicago Mercantile Exchange", 
    category: "market",
    desc: "Futures open interest, Fed funds probabilities, and SOFR term rates."
  },
  { 
    code: "CBOE", 
    name: "CBOE Global Markets", 
    category: "market",
    desc: "VIX index, put/call ratios, SKEW index, and volatility term structure."
  },
  { 
    code: "ICE", 
    name: "Intercontinental Exchange", 
    category: "market",
    desc: "Brent crude benchmarks, LIBOR/SOFR rates, and credit default swap indices."
  },
  { 
    code: "BALTIC", 
    name: "Baltic Exchange", 
    category: "market",
    desc: "Baltic Dry Index, shipping freight rates, and global trade flow indicators."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ALTERNATIVE DATA - SURVEYS & SENTIMENT
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "ISM", 
    name: "Institute for Supply Management", 
    category: "alt-data",
    desc: "Manufacturing & Services PMI, new orders, employment, and prices paid indices."
  },
  { 
    code: "CONFBOARD", 
    name: "Conference Board", 
    category: "alt-data",
    desc: "Consumer confidence, leading economic index (LEI), and CEO confidence survey."
  },
  { 
    code: "UMICH", 
    name: "U. of Michigan Surveys", 
    category: "alt-data",
    desc: "Consumer sentiment, inflation expectations, and buying conditions indices."
  },
  { 
    code: "AAII", 
    name: "American Assoc. of Individual Investors", 
    category: "alt-data",
    desc: "Weekly sentiment survey: bullish, bearish, and neutral investor readings."
  },
  { 
    code: "NFIB", 
    name: "Natl. Federation of Independent Business", 
    category: "alt-data",
    desc: "Small business optimism index, hiring plans, and capital expenditure intentions."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // REAL ESTATE & HOUSING
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "NAR", 
    name: "National Association of Realtors", 
    category: "real-estate",
    desc: "Existing home sales, pending home sales index, and median home prices."
  },
  { 
    code: "MBA", 
    name: "Mortgage Bankers Association", 
    category: "real-estate",
    desc: "Mortgage applications, refinance index, purchase index, and delinquency rates."
  },
  { 
    code: "CORELOGIC", 
    name: "CoreLogic", 
    category: "real-estate",
    desc: "Home price indices, negative equity data, and foreclosure statistics."
  },
  { 
    code: "ZILLOW", 
    name: "Zillow Research", 
    category: "real-estate",
    desc: "Zillow Home Value Index, rent indices, inventory levels, and days on market."
  },
  { 
    code: "NAHB", 
    name: "National Assoc. of Home Builders", 
    category: "real-estate",
    desc: "Housing market index (builder confidence), traffic of prospective buyers."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // SHIPPING & TRADE FLOWS
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "POLA", 
    name: "Port of Los Angeles", 
    category: "shipping",
    desc: "Container throughput (TEUs), import/export volumes, and vessel calls."
  },
  { 
    code: "POLB", 
    name: "Port of Long Beach", 
    category: "shipping",
    desc: "Monthly cargo statistics, rail volumes, and truck gate moves."
  },
  { 
    code: "FLEXPORT", 
    name: "Flexport Research", 
    category: "shipping",
    desc: "Ocean freight indices, port congestion metrics, and supply chain indicators."
  },
  { 
    code: "FREIGHTOS", 
    name: "Freightos Baltic Index", 
    category: "shipping",
    desc: "Global container shipping rates by trade lane and spot market indices."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // ENERGY & COMMODITIES
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "BAKER", 
    name: "Baker Hughes", 
    category: "energy",
    desc: "North American rig counts (oil & gas), international rig activity."
  },
  { 
    code: "API", 
    name: "American Petroleum Institute", 
    category: "energy",
    desc: "Weekly crude inventories, refinery utilization, and product supplies."
  },
  { 
    code: "OPEC", 
    name: "OPEC Monthly Reports", 
    category: "energy",
    desc: "Production quotas, compliance rates, demand forecasts, and spare capacity."
  },
  { 
    code: "IEA", 
    name: "International Energy Agency", 
    category: "energy",
    desc: "Global oil market reports, energy security assessments, and transition tracking."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CREDIT & CONSUMER
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "EQUIFAX", 
    name: "Equifax", 
    category: "credit",
    desc: "Consumer credit trends, delinquency rates, and lending market insights."
  },
  { 
    code: "EXPERIAN", 
    name: "Experian", 
    category: "credit",
    desc: "Auto loan originations, credit card balances, and consumer debt levels."
  },
  { 
    code: "TRANSUNION", 
    name: "TransUnion", 
    category: "credit",
    desc: "Credit utilization rates, new account openings, and serious delinquencies."
  },
  { 
    code: "NYFRB-SCE", 
    name: "NY Fed Consumer Expectations", 
    category: "credit",
    desc: "Inflation expectations, household spending, income growth, and credit access."
  },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // OTHER GOVERNMENT & SPECIALTY
  // ═══════════════════════════════════════════════════════════════════════════
  { 
    code: "NOAA", 
    name: "National Oceanic & Atmospheric Admin", 
    category: "other",
    desc: "Weather patterns, hurricane tracking, drought monitors affecting agriculture & energy."
  },
  { 
    code: "DOT", 
    name: "Dept. of Transportation", 
    category: "other",
    desc: "Vehicle miles traveled, airline passenger data, and freight transportation indices."
  },
  { 
    code: "TSA", 
    name: "TSA Checkpoint Data", 
    category: "other",
    desc: "Daily passenger throughput at U.S. airports vs. historical comparisons."
  },
  { 
    code: "REDBOOK", 
    name: "Redbook Research", 
    category: "other",
    desc: "Weekly same-store retail sales growth vs. prior year."
  },
];

// ============================================================================
// DATA VAULT: State & Fetching
// ============================================================================

/** @type {Object|null} Cached vault data */
let vaultDataCache = null;

/** @type {HTMLElement|null} Active tooltip element */
let activeTooltip = null;

/**
 * Fetches live vault data from CAP API /signals/freshness endpoint
 * Falls back to mock data if API call fails
 * @returns {Promise<Object>}
 */
async function fetchVaultData() {
  // Return cached data if available
  if (vaultDataCache) return vaultDataCache;
  
  try {
    // Fetch ALL the real data in parallel
    const [freshness, catalog, seriesData] = await Promise.all([
      fetchSignalsFreshness(),
      fetchCatalogStats().catch(e => { console.warn("[DataVault] Catalog fetch failed:", e); return null; }),
      fetchSeriesStats().catch(e => { console.warn("[DataVault] Series fetch failed:", e); return null; })
    ]);
    
    console.log("[DataVault] Freshness API response:", freshness);
    console.log("[DataVault] Catalog API response:", catalog);
    console.log("[DataVault] Series API response:", seriesData);
    
    // Get signal counts from freshness
    const currentCount = freshness.summary?.current || 0;
    const staleCount = freshness.summary?.stale || 0;
    const missingCount = freshness.summary?.missing || 0;
    const totalSignals = currentCount + staleCount + missingCount;
    
    // Get REAL series count from catalog
    const realSeriesCount = catalog?.series_count || totalSignals;
    const realSignalCount = catalog?.signal_count || totalSignals;
    
    // Calculate REAL coverage from series data
    let earliestYear = "1901"; // Default - we know you have data this far back
    if (seriesData?.data?.length > 0) {
      const years = seriesData.data
        .map(s => s.observation_start || s.last_updated)
        .filter(Boolean)
        .map(d => new Date(d).getFullYear())
        .filter(y => y > 1800 && y < 2100); // Sanity check
      
      if (years.length > 0) {
        earliestYear = String(Math.min(...years));
      }
    }
    
    // Check if data was synced recently (within last 24 hours)
    const checkedAt = freshness.checked_at ? new Date(freshness.checked_at) : null;
    const now = new Date();
    const hoursSinceSync = checkedAt ? (now - checkedAt) / (1000 * 60 * 60) : 999;
    
    // Pipeline is operational if sync was recent
    const status = hoursSinceSync < 24 ? "operational" : "degraded";
    
    // Count derived vs source signals
    const derivedSignals = freshness.signals?.filter(s => 
      s.signal_id?.includes('_ZSCORE') || 
      s.signal_id?.includes('_ROC') || 
      s.signal_id?.includes('_PCT') ||
      s.signal_id?.includes('_MA')
    ).length || 0;
    const sourceSignals = totalSignals - derivedSignals;
    
    vaultDataCache = {
      fred: {
        lastRun: freshness.checked_at || null,
        seriesCount: realSeriesCount,
        signalCount: realSignalCount,
        sourceSignals: sourceSignals,
        derivedSignals: derivedSignals,
        currentCount: currentCount,
        staleCount: staleCount,
        coverageStart: earliestYear,
        status: status
      },
      bls: {
        lastRun: null,
        programCount: 9,
        seriesEstimate: "43K+",
        recordEstimate: "2.5M+",
        coverageStart: "1950",
        status: "planned"
      },
      fredBulk: {
        monthlyCount: catalog?.categories?.find(c => c.category === 'monthly')?.series_count || 375,
        quarterlyCount: catalog?.categories?.find(c => c.category === 'quarterly')?.series_count || 248,
        coverageStart: earliestYear, // Use same earliest year
        status: "operational"
      },
      // Store totals for footer
      totals: {
        series: realSeriesCount,
        signals: realSignalCount,
        coverage: earliestYear
      },
      fetchedAt: new Date().toISOString(),
      isLive: true,
      _rawFreshness: freshness,
      _rawCatalog: catalog
    };
    
    console.log("[DataVault] Live data fetched successfully:", vaultDataCache);
    return vaultDataCache;
    
  } catch (error) {
    console.warn("[DataVault] API fetch failed, using mock data:", error);
    vaultDataCache = getMockVaultData();
    return vaultDataCache;
  }
}

/**
 * Returns mock vault data for development/fallback
 * @returns {Object}
 */
function getMockVaultData() {
  return {
    fred: {
      lastRun: "2024-12-24T01:15:32Z",
      seriesCount: 500,
      signalCount: 123,
      sourceSignals: 77,
      derivedSignals: 46,
      currentCount: 110,
      staleCount: 13,
      coverageStart: "1901",
      status: "operational"
    },
    bls: {
      lastRun: null,
      programCount: 9,
      seriesEstimate: "43K+",
      recordEstimate: "2.5M+",
      coverageStart: "1950",
      status: "planned"
    },
    fredBulk: {
      monthlyCount: 127,
      quarterlyCount: 248,
      coverageStart: "1959",
      status: "operational"
    },
    totals: {
      series: 875,
      signals: 123,
      coverage: "1901"
    },
    fetchedAt: new Date().toISOString(),
    isLive: false
  };
}

/**
 * Formats a date string for display
 * @param {string|null} isoString 
 * @returns {string}
 */
function formatVaultDate(isoString) {
  if (!isoString) return "—";
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "—";
  }
}

/**
 * Formats large numbers with K/M suffixes
 * @param {number} num 
 * @returns {string}
 */
function formatNumber(num) {
  if (typeof num === "string") return num;
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
}

// ============================================================================
// DATA VAULT: Tooltip System
// ============================================================================

/**
 * Creates and shows a tooltip for a data source chip
 * @param {HTMLElement} chip - The chip element
 * @param {Object} source - Source data object
 */
function showSourceTooltip(chip, source) {
  // Remove any existing tooltip
  hideSourceTooltip();
  
  // Category display names
  const categoryNames = {
    'govt': 'U.S. Government',
    'regulatory': 'Financial Regulatory',
    'central-bank': 'Central Bank',
    'international': 'International',
    'market': 'Market Data',
    'alt-data': 'Alternative Data',
    'real-estate': 'Real Estate',
    'shipping': 'Shipping & Trade',
    'energy': 'Energy & Commodities',
    'credit': 'Credit & Consumer',
    'other': 'Specialty Data'
  };
  
  // Create tooltip
  const tooltip = el("div", { className: "vault-tooltip" }, [
    el("div", { className: "vault-tooltip-header" }, [
      el("span", { className: "vault-tooltip-code" }, [source.code]),
      el("span", { className: `vault-tooltip-category vault-tooltip-category--${source.category}` }, [
        categoryNames[source.category] || source.category
      ])
    ]),
    el("div", { className: "vault-tooltip-name" }, [source.name]),
    el("div", { className: "vault-tooltip-desc" }, [source.desc]),
    el("div", { className: "vault-tooltip-status" }, [
      el("span", { className: "vault-tooltip-status-dot" }, []),
      "Coming Q1 2026"
    ])
  ]);
  
  // Position tooltip
  document.body.appendChild(tooltip);
  
  const chipRect = chip.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();
  
  // Calculate position (prefer above, fallback to below)
  let top = chipRect.top - tooltipRect.height - 10;
  let left = chipRect.left + (chipRect.width / 2) - (tooltipRect.width / 2);
  
  // Flip to below if not enough space above
  if (top < 10) {
    top = chipRect.bottom + 10;
    tooltip.classList.add("vault-tooltip--below");
  }
  
  // Keep within viewport horizontally
  if (left < 10) left = 10;
  if (left + tooltipRect.width > window.innerWidth - 10) {
    left = window.innerWidth - tooltipRect.width - 10;
  }
  
  tooltip.style.top = `${top}px`;
  tooltip.style.left = `${left}px`;
  
  // Animate in
  requestAnimationFrame(() => {
    tooltip.classList.add("vault-tooltip--visible");
  });
  
  activeTooltip = tooltip;
}

/**
 * Hides the active tooltip
 */
function hideSourceTooltip() {
  if (activeTooltip) {
    activeTooltip.remove();
    activeTooltip = null;
  }
}

// ============================================================================
// DATA VAULT: Component
// ============================================================================

/**
 * Creates the Data Vault section
 * @returns {HTMLElement}
 */
function createDataVault() {
  const container = el("section", { className: "data-vault" }, [
    // Header
    el("div", { className: "vault-header" }, [
      el("div", { className: "vault-title-row" }, [
        el("div", { className: "vault-icon" }, []),
        el("h2", { className: "vault-title" }, ["Data Vault"]),
        el("span", { className: "vault-status vault-status--loading" }, ["INITIALIZING..."])
      ]),
      el("p", { className: "vault-subtitle" }, [
        "Live infrastructure powering CAPITULATORS.IO signal intelligence"
      ])
    ]),
    
    // Main content wrapper
    el("div", { className: "vault-content" }, [
      // Data sources table
      el("div", { className: "vault-table-wrap" }, [
        el("table", { className: "vault-table" }, [
          el("thead", {}, [
            el("tr", {}, [
              el("th", {}, ["Source"]),
              el("th", {}, ["Series"]),
              el("th", {}, ["Coverage"]),
              el("th", {}, ["Last Sync"]),
              el("th", {}, ["Status"])
            ])
          ]),
          el("tbody", { className: "vault-table-body" }, [
            // Rows will be populated dynamically
          ])
        ])
      ]),
      
      // Planned sources grid (compact display)
      el("div", { className: "vault-roadmap" }, [
        el("div", { className: "vault-roadmap-header" }, [
          el("span", { className: "vault-roadmap-title" }, ["Ingestion Roadmap"]),
          el("span", { className: "vault-roadmap-count" }, [`${PLANNED_SOURCES.length} sources planned`])
        ]),
        el("div", { className: "vault-roadmap-grid" }, [])
      ]),
      
      // Pipeline stats footer
      el("div", { className: "vault-footer" }, [
        el("div", { className: "vault-stats" }, [
          el("span", { className: "vault-stat" }, [
            el("span", { className: "vault-stat-value", id: "vault-stat-signals" }, ["—"]),
            el("span", { className: "vault-stat-label" }, ["signals"])
          ]),
          el("span", { className: "vault-stat-divider" }, ["│"]),
          el("span", { className: "vault-stat" }, [
            el("span", { className: "vault-stat-value vault-stat-value--status", id: "vault-stat-status" }, ["—"]),
            el("span", { className: "vault-stat-label" }, ["status"])
          ]),
          el("span", { className: "vault-stat-divider" }, ["│"]),
          el("span", { className: "vault-stat" }, [
            el("span", { className: "vault-stat-value", id: "vault-stat-history" }, ["—"]),
            el("span", { className: "vault-stat-label" }, ["history"])
          ]),
          el("span", { className: "vault-stat-divider" }, ["│"]),
          el("span", { className: "vault-stat" }, [
            el("span", { className: "vault-stat-value" }, ["Daily"]),
            el("span", { className: "vault-stat-label" }, ["refresh"])
          ])
        ]),
        el("div", { className: "vault-meta" }, [
          el("span", { className: "vault-meta-text" }, [""])
        ])
      ])
    ])
  ]);
  
  // Add vault icon SVG
  const iconWrap = container.querySelector(".vault-icon");
  if (iconWrap) {
    iconWrap.innerHTML = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M4 7V4h16v3M9 20h6M12 4v16"/>
        <rect x="2" y="7" width="20" height="10" rx="2"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>
    `;
  }
  
  // Populate roadmap grid with interactive chips
  const roadmapGrid = container.querySelector(".vault-roadmap-grid");
  if (roadmapGrid) {
    PLANNED_SOURCES.forEach(src => {
      const chip = el("span", { 
        className: `vault-roadmap-chip vault-roadmap-chip--${src.category}`
      }, [src.code]);
      
      // Add hover events for tooltip
      chip.addEventListener("mouseenter", () => showSourceTooltip(chip, src));
      chip.addEventListener("mouseleave", hideSourceTooltip);
      
      roadmapGrid.appendChild(chip);
    });
  }
  
  // Load data and populate
  loadVaultData(container);
  
  return container;
}

/**
 * Loads vault data and updates the UI
 * @param {HTMLElement} container 
 */
async function loadVaultData(container) {
  const statusEl = container.querySelector(".vault-status");
  const tbody = container.querySelector(".vault-table-body");
  const metaText = container.querySelector(".vault-meta-text");
  
  try {
    const data = await fetchVaultData();
    
    // Update status indicator
    if (statusEl) {
      statusEl.className = "vault-status vault-status--operational";
      statusEl.textContent = data.isLive ? "● LIVE" : "● CACHED";
    }
    
    // Build table rows - only show live sources in table
    if (tbody) {
      tbody.innerHTML = "";
      
      // FRED Direct row - show real source + derived counts
      const fredSeriesText = data.fred.derivedSignals > 0 
        ? `${data.fred.sourceSignals} + ${data.fred.derivedSignals} computed`
        : `${data.fred.seriesCount}`;
      
      tbody.appendChild(createVaultRow({
        source: "FRED",
        sourceClass: "fred",
        description: "Federal Reserve Economic Data",
        series: fredSeriesText,
        coverage: `${data.fred.coverageStart}–now`,
        lastSync: formatVaultDate(data.fred.lastRun),
        status: data.fred.status
      }));
      
      // FRED Bulk row
      tbody.appendChild(createVaultRow({
        source: "FRED-MD/QD",
        sourceClass: "fred",
        description: "Macro Datasets (Monthly/Quarterly)",
        series: `${data.fredBulk.monthlyCount + data.fredBulk.quarterlyCount}`,
        coverage: `${data.fredBulk.coverageStart}–now`,
        lastSync: "Monthly",
        status: data.fredBulk.status
      }));
      
      // BLS row
      tbody.appendChild(createVaultRow({
        source: "BLS",
        sourceClass: "bls",
        description: "Bureau of Labor Statistics",
        series: data.bls.seriesEstimate,
        coverage: `${data.bls.coverageStart}–now`,
        lastSync: formatVaultDate(data.bls.lastRun),
        status: data.bls.status
      }));
    }
    
    // Update meta text
    if (metaText) {
      const fetchTime = new Date(data.fetchedAt).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      });
      metaText.textContent = data.isLive 
        ? `Last checked: ${fetchTime}` 
        : "Using cached data";
    }
    
    // Update footer stats - use real totals
    const signalsStat = document.getElementById("vault-stat-signals");
    const statusStat = document.getElementById("vault-stat-status");
    const historyStat = document.getElementById("vault-stat-history");
    
    if (signalsStat) {
      // Use real totals from catalog if available
      const totalSignals = data.totals?.series || 
        (data.fred.seriesCount + (data.fredBulk?.monthlyCount || 0) + (data.fredBulk?.quarterlyCount || 0));
      signalsStat.textContent = `${totalSignals}+`;
    }
    
    if (historyStat) {
      // Calculate years of history from earliest coverage
      const earliestYear = parseInt(data.totals?.coverage || data.fred.coverageStart || "1960");
      const currentYear = new Date().getFullYear();
      const yearsOfHistory = currentYear - earliestYear;
      historyStat.textContent = `${yearsOfHistory}yr`;
    }
    
    if (statusStat) {
      if (data.fred.status === "operational") {
        statusStat.textContent = "Current";
        statusStat.style.color = "var(--tg-fog-green)";
      } else {
        statusStat.textContent = "Stale";
        statusStat.style.color = "var(--tg-rust-red)";
      }
    }
    
  } catch (error) {
    console.error("[DataVault] Failed to load:", error);
    if (statusEl) {
      statusEl.className = "vault-status vault-status--error";
      statusEl.textContent = "● ERROR";
    }
  }
}

/**
 * Creates a single vault table row
 * @param {Object} config 
 * @returns {HTMLElement}
 */
function createVaultRow({ source, sourceClass, description, series, coverage, lastSync, status }) {
  const statusClass = status === "operational" ? "operational" 
    : status === "planned" ? "planned" 
    : "degraded";
  
  const statusText = status === "operational" ? "● Live"
    : status === "planned" ? "○ Planned"
    : "◐ Degraded";
  
  return el("tr", { className: `vault-row vault-row--${sourceClass}` }, [
    el("td", { className: "vault-cell vault-cell--source" }, [
      el("span", { className: "vault-source-code" }, [source]),
      el("span", { className: "vault-source-name" }, [description])
    ]),
    el("td", { className: "vault-cell vault-cell--series" }, [series]),
    el("td", { className: "vault-cell vault-cell--coverage" }, [coverage]),
    el("td", { className: "vault-cell vault-cell--sync" }, [lastSync]),
    el("td", { className: "vault-cell vault-cell--status" }, [
      el("span", { className: `vault-status-badge vault-status-badge--${statusClass}` }, [statusText])
    ])
  ]);
}

// ============================================================================
// MARKET PULSE: Live Movers Widget
// ============================================================================

/**
 * Calculates regime from signal data (client-side CSI logic)
 * Simplified version of the signals page calculation
 * @param {Object[]} signals - Array of signal objects
 * @returns {Object} Regime info
 */
function calculateRegime(signals) {
  // Count signals by level
  const counts = {
    extreme: 0,
    high: 0,
    elevated: 0,
    normal: 0,
    low: 0
  };
  
  signals.forEach(s => {
    const level = s.level?.toLowerCase();
    if (level && counts.hasOwnProperty(level)) {
      counts[level]++;
    }
  });
  
  // Simple stress score: weighted by level severity
  const weights = { extreme: 100, high: 75, elevated: 50, normal: 25, low: 10 };
  let totalWeight = 0;
  let weightedSum = 0;
  
  Object.entries(counts).forEach(([level, count]) => {
    weightedSum += count * weights[level];
    totalWeight += count;
  });
  
  const score = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 25;
  
  // Determine regime from score
  let regime, regimeClass;
  if (score >= 75) {
    regime = 'CRISIS';
    regimeClass = 'crisis';
  } else if (score >= 60) {
    regime = 'HIGH STRESS';
    regimeClass = 'high';
  } else if (score >= 45) {
    regime = 'ELEVATED';
    regimeClass = 'elevated';
  } else if (score >= 30) {
    regime = 'NORMAL';
    regimeClass = 'normal';
  } else {
    regime = 'CALM';
    regimeClass = 'calm';
  }
  
  return {
    regime,
    regimeClass,
    score,
    counts,
    totalSignals: totalWeight
  };
}

/**
 * Creates the regime status banner
 * @returns {HTMLElement}
 */
/**
 * Creates a combined status bar with regime + stats in one compact row
 * @returns {HTMLElement}
 */
function createCombinedStatusBar() {
  const bar = el("div", { className: "combined-status-bar", id: "combined-status-bar" }, [
    el("div", { className: "status-bar-content" }, [
      // Regime section
      el("div", { className: "status-bar-regime" }, [
        el("span", { className: "status-bar-label" }, ["REGIME"]),
        el("span", { className: "status-bar-regime-value status-bar-regime-value--loading" }, ["..."]),
        el("span", { className: "status-bar-csi" }, [""])
      ]),
      el("span", { className: "status-bar-divider" }, []),
      // Stats section
      el("div", { className: "status-bar-stats" }, [
        el("span", { className: "status-bar-stat status-bar-stat--total" }, [
          el("span", { className: "stat-value" }, ["—"]),
          el("span", { className: "stat-label" }, ["signals"])
        ]),
        el("span", { className: "status-bar-stat status-bar-stat--stressed" }, [
          el("span", { className: "stat-value" }, ["—"]),
          el("span", { className: "stat-label" }, ["stressed"])
        ]),
        el("span", { className: "status-bar-stat status-bar-stat--elevated" }, [
          el("span", { className: "stat-value" }, ["—"]),
          el("span", { className: "stat-label" }, ["elevated"])
        ]),
        el("span", { className: "status-bar-stat status-bar-stat--stable" }, [
          el("span", { className: "stat-value" }, ["—"]),
          el("span", { className: "stat-label" }, ["stable"])
        ])
      ])
    ])
  ]);
  
  // Load data async
  loadCombinedStatusBar(bar);
  
  return bar;
}

/**
 * Loads and updates the combined status bar
 * @param {HTMLElement} bar
 */
async function loadCombinedStatusBar(bar) {
  const regimeValueEl = bar.querySelector(".status-bar-regime-value");
  const csiEl = bar.querySelector(".status-bar-csi");
  
  try {
    const data = await fetchSignalsLatest();
    console.log("[StatusBar] Latest signals response:", data);
    
    const regime = calculateRegime(data.signals || []);
    console.log("[StatusBar] Calculated regime:", regime);
    
    // Update regime
    regimeValueEl.textContent = regime.regime;
    regimeValueEl.className = `status-bar-regime-value status-bar-regime-value--${regime.regimeClass}`;
    csiEl.textContent = `CSI: ${regime.score}`;
    
    // Update stats
    const counts = regime.counts;
    const total = regime.totalSignals;
    const stressed = (counts.extreme || 0) + (counts.high || 0);
    const elevated = counts.elevated || 0;
    const stable = (counts.normal || 0) + (counts.low || 0);
    
    const totalEl = bar.querySelector(".status-bar-stat--total .stat-value");
    const stressedEl = bar.querySelector(".status-bar-stat--stressed .stat-value");
    const elevatedEl = bar.querySelector(".status-bar-stat--elevated .stat-value");
    const stableEl = bar.querySelector(".status-bar-stat--stable .stat-value");
    
    if (totalEl) totalEl.textContent = total.toString();
    if (stressedEl) stressedEl.textContent = stressed.toString();
    if (elevatedEl) elevatedEl.textContent = elevated.toString();
    if (stableEl) stableEl.textContent = stable.toString();
    
    console.log("[StatusBar] Updated:", { regime: regime.regime, total, stressed, elevated, stable });
    
  } catch (error) {
    console.error("[StatusBar] Failed to load:", error);
    regimeValueEl.textContent = "UNAVAILABLE";
    regimeValueEl.className = "status-bar-regime-value status-bar-regime-value--error";
  }
}

// Keep old functions for backward compatibility but mark deprecated
function createRegimeBanner() {
  // DEPRECATED - use createCombinedStatusBar instead
  return createCombinedStatusBar();
}

function createQuickStatsBar() {
  // DEPRECATED - merged into createCombinedStatusBar
  return el("div", { style: "display:none" }, []);
}

/**
 * Creates the Market Pulse widget showing today's biggest movers
 * @returns {HTMLElement}
 */
function createMarketPulse() {
  const container = el("section", { className: "market-pulse" }, [
    el("div", { className: "market-pulse-header" }, [
      el("h2", { className: "market-pulse-title" }, ["Market Pulse"]),
      el("span", { className: "market-pulse-period" }, ["24H"])
    ]),
    el("div", { className: "market-pulse-content" }, [
      el("div", { className: "market-pulse-loading" }, [
        el("span", { className: "loading-spinner" }, []),
        " Loading movers..."
      ])
    ])
  ]);
  
  // Load data async
  loadMarketPulse(container);
  
  return container;
}

/**
 * Loads and renders market pulse data
 * @param {HTMLElement} container
 */
async function loadMarketPulse(container) {
  const content = container.querySelector(".market-pulse-content");
  
  try {
    const movers = await fetchMovers({ period: '1d', limit: 3 });
    console.log("[MarketPulse] Movers API response:", movers);
    
    // Check if we have data
    const hasGainers = movers.gainers && movers.gainers.length > 0;
    const hasLosers = movers.losers && movers.losers.length > 0;
    
    if (!hasGainers && !hasLosers) {
      // Fallback: Show top stressed signals instead
      console.log("[MarketPulse] No movers, fetching top stressed signals...");
      await loadTopStressedSignals(container, content);
      return;
    }
    
    content.innerHTML = '';
    content.appendChild(
      el("div", { className: "market-pulse-grid" }, [
        // Gainers column
        el("div", { className: "market-pulse-column market-pulse-column--up" }, [
          el("div", { className: "market-pulse-column-header" }, ["▲ Rising"]),
          ...(movers.gainers || []).map(s => createMoverCard(s, 'up'))
        ]),
        // Losers column
        el("div", { className: "market-pulse-column market-pulse-column--down" }, [
          el("div", { className: "market-pulse-column-header" }, ["▼ Falling"]),
          ...(movers.losers || []).map(s => createMoverCard(s, 'down'))
        ])
      ])
    );
    
    // Load sparklines for each mover
    const allMovers = [...(movers.gainers || []), ...(movers.losers || [])];
    for (const signal of allMovers) {
      loadMoverSparkline(signal.signal_id);
    }
    
    console.log("[MarketPulse] Rendered successfully");
    
  } catch (error) {
    console.error("[MarketPulse] Failed to load:", error);
    content.innerHTML = '';
    content.appendChild(
      el("div", { className: "market-pulse-error" }, [
        "Unable to load market data"
      ])
    );
  }
}

/**
 * Loads top stressed signals as fallback when no movers
 * @param {HTMLElement} container - Market pulse container
 * @param {HTMLElement} content - Content area to populate
 */
async function loadTopStressedSignals(container, content) {
  try {
    // Update title to indicate we're showing stressed signals
    const title = container.querySelector(".market-pulse-title");
    if (title) title.textContent = "Signals to Watch";
    
    const period = container.querySelector(".market-pulse-period");
    if (period) period.textContent = "STRESS";
    
    // Fetch top stressed by z-score
    const rankings = await fetchRankings({ by: 'z_score', order: 'desc', limit: 6 });
    console.log("[MarketPulse] Rankings response:", rankings);
    
    if (!rankings.rankings || rankings.rankings.length === 0) {
      content.innerHTML = '';
      content.appendChild(
        el("div", { className: "market-pulse-empty" }, [
          "All signals within normal ranges"
        ])
      );
      return;
    }
    
    content.innerHTML = '';
    content.appendChild(
      el("div", { className: "market-pulse-stressed-list" }, 
        rankings.rankings.map(signal => createStressedSignalCard(signal))
      )
    );
    
    // Load sparklines for stressed signals
    for (const signal of rankings.rankings) {
      loadMoverSparkline(signal.signal_id);
    }
    
    console.log("[MarketPulse] Rendered stressed signals");
    
  } catch (error) {
    console.error("[MarketPulse] Failed to load stressed signals:", error);
    content.innerHTML = '';
    content.appendChild(
      el("div", { className: "market-pulse-empty" }, [
        "Markets calm - no elevated signals"
      ])
    );
  }
}

/**
 * Creates a card for a stressed signal
 * @param {Object} signal - Signal from rankings API
 * @returns {HTMLElement}
 */
function createStressedSignalCard(signal) {
  const zScore = signal.z_score?.toFixed(1) || '—';
  const level = signal.level || 'normal';
  
  // Color based on level
  let levelClass = 'normal';
  if (level === 'extreme' || level === 'crisis') levelClass = 'extreme';
  else if (level === 'high' || level === 'stressed') levelClass = 'high';
  else if (level === 'elevated') levelClass = 'elevated';
  
  return el("a", { 
    className: "stressed-signal-card",
    href: "#/signals",
    title: `View ${signal.name || signal.signal_id} in Signals`
  }, [
    el("div", { className: "stressed-signal-header" }, [
      el("span", { className: "stressed-signal-id" }, [signal.signal_id]),
      el("span", { className: `stressed-signal-zscore stressed-signal-zscore--${levelClass}` }, [
        `z: ${zScore}`
      ])
    ]),
    el("div", { className: "stressed-signal-name" }, [signal.name || signal.signal_id]),
    el("div", { className: "mover-card-sparkline", id: `sparkline-${signal.signal_id}` }, [])
  ]);
}

/**
 * Creates a single mover card
 * @param {Object} signal - Signal data from movers API
 * @param {'up'|'down'} direction
 * @returns {HTMLElement}
 */
function createMoverCard(signal, direction) {
  const changeClass = direction === 'up' ? 'change--up' : 'change--down';
  const arrow = direction === 'up' ? '▲' : '▼';
  const changePct = Math.abs(signal.change_pct || 0).toFixed(1);
  
  return el("div", { className: "mover-card" }, [
    el("div", { className: "mover-card-header" }, [
      el("span", { className: "mover-card-id" }, [signal.signal_id]),
      el("span", { className: `mover-card-change ${changeClass}` }, [
        `${arrow} ${changePct}%`
      ])
    ]),
    el("div", { className: "mover-card-name" }, [signal.name || signal.signal_id]),
    el("div", { className: "mover-card-sparkline", id: `sparkline-${signal.signal_id}` }, [])
  ]);
}

/**
 * Loads sparkline for a mover card
 * @param {string} signalId
 */
async function loadMoverSparkline(signalId) {
  const container = document.getElementById(`sparkline-${signalId}`);
  if (!container) return;
  
  try {
    const data = await fetchSparkline(signalId, 14); // 2 weeks of data
    const direction = (data.change_pct || 0) >= 0 ? 'up' : 'down';
    renderMiniSparkline(container, data.points || [], direction);
  } catch (error) {
    console.warn(`[Sparkline] Failed for ${signalId}:`, error);
    container.innerHTML = '<span class="sparkline-error">—</span>';
  }
}

/**
 * Renders a tiny sparkline SVG
 * @param {HTMLElement} container
 * @param {number[]} points
 * @param {'up'|'down'} direction
 */
function renderMiniSparkline(container, points, direction) {
  if (!points || points.length < 2) {
    container.innerHTML = '<span class="sparkline-empty">—</span>';
    return;
  }
  
  const width = 60;
  const height = 20;
  const color = direction === 'up' ? 'var(--tg-fog-green)' : 'var(--tg-rust-red)';
  
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  
  const pathData = points.map((val, i) => {
    const x = (i / (points.length - 1)) * width;
    const y = height - ((val - min) / range) * (height - 2) - 1; // 1px padding
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  
  container.innerHTML = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" class="mini-sparkline">
      <path d="${pathData}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  `;
}

// ============================================================================
// DATA: Featured Dispatches (Articles)
// ============================================================================

/**
 * @typedef {Object} Dispatch
 * @property {string} id - Unique identifier
 * @property {string} category - Display category
 * @property {string} categoryType - CSS class suffix
 * @property {string} date - Publication date
 * @property {string} title - Article title
 * @property {string} summary - Brief description
 * @property {string} readTime - Estimated read time
 * @property {boolean} isPinned - Whether pinned to top
 * @property {string} [externalUrl] - External link (e.g., X post)
 */

// ============================================================================
// X POST EMBEDS - Custom styled (easy to remove - just delete this section)
// ============================================================================

/**
 * @typedef {Object} XPost
 * @property {string} id - Tweet ID
 * @property {string} handle - @handle
 * @property {string} displayName - Display name
 * @property {string} date - Post date
 * @property {string} content - Post text content
 * @property {string} url - Full URL to post
 * @property {Object} [stats] - Engagement stats
 */

/** @type {XPost[]} */
const FEATURED_X_POSTS = [
  {
    id: "2004325484230754570",
    handle: "@CapitulationStn",
    displayName: "Macro Isn't Complicated—It's Just Hidden",
    date: "Dec 2025",
    content: "Price is the last thing to move. The true drivers lurk below: liquidity conditions, funding stress, policy constraints, balance sheet pressure.\n\nThe data is public. The insight isn't. That's the gap we're closing.",
    url: "https://x.com/CapitulationStn/status/2004325484230754570",
  }
];

/**
 * Creates an X post card with gothic terminal styling
 * Styled to match dispatch cards
 * @param {XPost} post
 * @returns {HTMLElement}
 */
function createXPostCard(post) {
  const card = el("a", {
    href: post.url,
    target: "_blank",
    rel: "noopener noreferrer",
    className: "widget-card dispatch-card dispatch-card--primary x-post-card"
  }, [
    // Icon (X logo)
    el("div", { className: "card-icon-wrap x-post-icon" }, [
      el("span", { className: "x-post-logo" }, ["𝕏"])
    ]),
    
    // Title (display name + handle)
    el("h3", { className: "widget-title" }, [post.displayName]),
    
    // Content as caption
    el("p", { className: "widget-caption" }, [post.content]),
    
    // Footer matching dispatch cards
    el("div", { className: "card-footer" }, [
      el("div", { className: "card-tags" }, [
        el("span", { className: "card-tag card-tag--x" }, [post.handle]),
        el("span", { className: "card-tag card-tag--date" }, [post.date]),
      ]),
      el("span", { className: "status-pill status-x" }, ["VIEW ON X →"])
    ])
  ]);
  
  return card;
}

// ============================================================================
// YOUTUBE PLAYLIST CARDS
// ============================================================================

/**
 * @typedef {Object} YouTubePlaylist
 * @property {string} id - Unique identifier
 * @property {string} title - Playlist title
 * @property {string} description - Brief description
 * @property {string} url - Full playlist URL
 * @property {string} videoCount - Number of videos (e.g., "2 videos")
 * @property {boolean} isComingSoon - Whether this is a placeholder
 * @property {string} [comingSoonDate] - When it's expected (e.g., "Jan 2026")
 */

/** @type {YouTubePlaylist[]} */
const FEATURED_YT_PLAYLISTS = [
  {
    id: "watcher-night-signals",
    title: "The Watcher — Night Signals",
    description: "Macro Thrash Metal. Late-night market dispatches set to a darker frequency.",
    url: "https://www.youtube.com/playlist?list=PL8kQ5hHcgZDAjoPCeUrw7PkxprQnXgMbs",
    videoCount: "2 videos",
    isComingSoon: false
  },
  {
    id: "signal-deep-dives",
    title: "Signal Deep Dives",
    description: "In-depth breakdowns of individual signals — what to watch, historical correlations, and the mechanics that matter.",
    url: null,
    videoCount: "Coming soon",
    isComingSoon: true,
    comingSoonDate: "Jan 2026"
  }
];

/**
 * Creates a YouTube playlist card
 * @param {YouTubePlaylist} playlist
 * @returns {HTMLElement}
 */
function createYouTubePlaylistCard(playlist) {
  const isDisabled = playlist.isComingSoon;
  
  const card = el(isDisabled ? "div" : "a", {
    href: isDisabled ? undefined : playlist.url,
    target: isDisabled ? undefined : "_blank",
    rel: isDisabled ? undefined : "noopener noreferrer",
    className: `widget-card dispatch-card yt-playlist-card ${isDisabled ? 'yt-playlist-card--coming-soon' : ''}`
  }, [
    // Icon (YouTube logo)
    el("div", { className: "card-icon-wrap yt-playlist-icon" }, [
      el("span", { className: "yt-logo" }, ["▶"])
    ]),
    
    // Title
    el("h3", { className: "widget-title" }, [playlist.title]),
    
    // Description
    el("p", { className: "widget-caption" }, [playlist.description]),
    
    // Footer
    el("div", { className: "card-footer" }, [
      el("div", { className: "card-tags" }, [
        el("span", { className: "card-tag card-tag--yt" }, ["@capitulators"]),
        el("span", { className: "card-tag card-tag--count" }, [playlist.videoCount])
      ]),
      el("span", { className: `status-pill ${isDisabled ? 'status-coming-soon' : 'status-yt'}` }, [
        isDisabled ? `COMING ${playlist.comingSoonDate}` : "WATCH ON YOUTUBE →"
      ])
    ])
  ]);
  
  return card;
}

/** @type {Dispatch[]} */
const FEATURED_DISPATCHES = [
  // Placeholder articles removed - will be populated with real content
  // Add new dispatches here as they're published
];

// ============================================================================
// COMPONENT: Dispatch Card (Article)
// ============================================================================

/**
 * Creates an SVG icon for dispatch cards based on category
 */
function createCategoryIcon(categoryType) {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.5");
  
  const icons = {
    liquidity: '<path d="M12 2v6m0 8v6M4.93 4.93l4.24 4.24m5.66 5.66l4.24 4.24M2 12h6m8 0h6M4.93 19.07l4.24-4.24m5.66-5.66l4.24-4.24"/>',
    fx: '<circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
    credit: '<path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>',
    macro: '<circle cx="12" cy="12" r="3"/><path d="M12 1v4m0 14v4M4.22 4.22l2.83 2.83m9.9 9.9l2.83 2.83M1 12h4m14 0h4M4.22 19.78l2.83-2.83m9.9-9.9l2.83-2.83"/>',
    china: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 3v18"/>',
    rates: '<path d="M3 3v18h18"/><path d="M7 14l4-4 4 4 5-5"/>',
  };
  
  svg.innerHTML = icons[categoryType] || icons.macro;
  return svg;
}

/**
 * Creates a dispatch article card - Observatory style
 * @param {Dispatch} dispatch - Dispatch data
 * @returns {HTMLElement}
 */
function createDispatchCard(dispatch) {
  const statusClass = dispatch.isPinned ? "status-pinned" : "status-article";
  const statusText = dispatch.isPinned ? "📌 PINNED" : "ARTICLE";
  
  const cardClasses = [
    "widget-card",
    "dispatch-card",
    dispatch.isPinned ? "dispatch-card--primary" : ""
  ].filter(Boolean).join(" ");

  const cardContent = [
    el("div", { className: "card-icon-wrap" }, []),
    el("h3", { className: "widget-title" }, [dispatch.title]),
    el("p", { className: "widget-caption" }, [dispatch.summary]),
    el("div", { className: "card-footer" }, [
      el("div", { className: "card-tags" }, [
        el("span", { className: `card-tag card-tag--${dispatch.categoryType}` }, [dispatch.category]),
        el("span", { className: "card-tag card-tag--date" }, [dispatch.date]),
        el("span", { className: "card-tag card-tag--time" }, [dispatch.readTime])
      ]),
      el("span", { className: `status-pill ${statusClass}` }, [statusText])
    ])
  ];

  // Create card as link
  const card = el("a", { 
    href: dispatch.externalUrl || `#/dispatches/${dispatch.id}`, 
    className: cardClasses,
    target: dispatch.externalUrl ? "_blank" : undefined,
    rel: dispatch.externalUrl ? "noopener noreferrer" : undefined
  }, cardContent);

  // Add icon
  const iconWrap = card.querySelector(".card-icon-wrap");
  if (iconWrap) {
    iconWrap.appendChild(createCategoryIcon(dispatch.categoryType));
  }

  return card;
}

// ============================================================================
// MAIN RENDER
// ============================================================================

/**
 * Renders the Dispatches page
 * @returns {HTMLElement}
 */
export function renderDispatchesPage() {
  // Sort dispatches - pinned first
  const sortedDispatches = [...FEATURED_DISPATCHES].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return 0;
  });

  return el("div", { className: "dispatches-page" }, [
    // Background layers (reuse observatory background)
    el("div", { className: "dispatches-bg-layer" }, []),
    el("div", { className: "dispatches-overlay" }, []),
    
    // Content
    el("div", { className: "dispatches-page-content" }, [
      // Hero Section
      el("section", { className: "dispatches-hero" }, [
        el("h1", { className: "dispatches-hero-title" }, ["Dispatches"]),
        el("p", { className: "dispatches-hero-tagline" }, [
          "Analysis from the Macro Watchtower"
        ]),
        el("p", { className: "dispatches-hero-description" }, [
          "Long-form analysis where dead narratives are buried and uncomfortable truths are exhumed. Regimes, mechanics, and the signals beneath the headlines."
        ])
      ]),
      
      // Combined Status Bar - regime + stats in one compact row
      createCombinedStatusBar(),
      
      // Dispatches Grid Section - THE MAIN CONTENT
      el("section", { className: "dispatches-grid-section" }, [
        el("div", { className: "dispatches-grid" }, [
          // X Post embeds first (featured)
          ...FEATURED_X_POSTS.map(post => createXPostCard(post)),
          // YouTube playlist cards
          ...FEATURED_YT_PLAYLISTS.map(playlist => createYouTubePlaylistCard(playlist)),
          // Then regular dispatches (when we have them)
          ...sortedDispatches.map(dispatch => createDispatchCard(dispatch))
        ])
      ]),
      
      // Market Pulse - moved below articles
      createMarketPulse(),

      // Coming Soon Note
      el("section", { className: "dispatches-coming-soon" }, [
        el("div", { className: "coming-soon-card" }, [
          el("span", { className: "coming-soon-icon" }, ["📜"]),
          el("h3", {}, ["More Dispatches Coming"]),
          el("p", {}, [
            "We're building out our analysis archive. Follow ",
            el("a", { 
              href: "https://x.com/CapitulationStn", 
              target: "_blank",
              rel: "noopener noreferrer"
            }, ["@CapitulationStn"]),
            " on X for real-time macro commentary."
          ])
        ])
      ]),
      
      // Data Vault Section
      createDataVault(),

      // Newsletter Section
      el("section", { className: "dispatches-subscribe-section" }, [
        el("div", { className: "subscribe-card" }, [
          el("h2", {}, ["Never Miss a Dispatch"]),
          el("p", {}, ["Get our analysis delivered to your inbox. No spam, no shilling — just macro."]),
          el("div", { className: "subscribe-form" }, [
            el("input", { 
              type: "email", 
              placeholder: "your@email.com",
              className: "subscribe-email-input",
              disabled: true
            }, []),
            el("button", { className: "btn-primary subscribe-btn", disabled: true }, ["Subscribe"])
          ]),
          el("p", { className: "subscribe-note" }, ["(Newsletter coming soon)"])
        ])
      ])
    ])
  ]);
}

/**
 * Cleanup function for dispatches page
 */
export function cleanupDispatchesPage() {
  vaultDataCache = null;
  hideSourceTooltip();
  console.log('[Dispatches] Cleanup complete');
}

// Export data for potential CMS integration
export { FEATURED_DISPATCHES };
